import { supabase } from '../supabase';
import { config } from '../config';
import type { Batch, BatchWithProduct, BatchInsert, BatchUpdate } from '../types';

/**
 * Get all batches for a product
 */
export const getBatchesByProduct = async (productId: string): Promise<BatchWithProduct[]> => {
  // 🔧 DEVELOPMENT MODE - Return mock batches
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Returning mock batches for product:', productId);
    
    const mockBatches: Record<string, BatchWithProduct[]> = {
      'product-1': [
        {
          id: 'batch-1-1',
          product_id: 'product-1',
          batch_no: 'BD-2024-Q4-001',
          received_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          expiry_date: new Date(Date.now() + 335 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'in_stock',
          coa_url: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          product: {
            id: 'product-1',
            sku: 'MWL-001',
            product_name: 'Blue Dream',
            brand: 'Premium Cannabis Co',
            form: 'flower',
            thc_pct: 18.5,
            cbd_pct: 0.5,
            pack_size: '3.5g',
            active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        },
        {
          id: 'batch-1-2',
          product_id: 'product-1',
          batch_no: 'BD-2024-Q3-045',
          received_at: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
          expiry_date: new Date(Date.now() + 275 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'low_stock',
          coa_url: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          product: {
            id: 'product-1',
            sku: 'MWL-001',
            product_name: 'Blue Dream',
            brand: 'Premium Cannabis Co',
            form: 'flower',
            thc_pct: 18.5,
            cbd_pct: 0.5,
            pack_size: '3.5g',
            active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        },
      ],
      'product-2': [
        {
          id: 'batch-2-1',
          product_id: 'product-2',
          batch_no: 'NL-2024-Q4-003',
          received_at: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
          expiry_date: new Date(Date.now() + 345 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'in_stock',
          coa_url: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          product: {
            id: 'product-2',
            sku: 'MWL-002',
            product_name: 'Northern Lights',
            brand: 'Green Leaf Co',
            form: 'flower',
            thc_pct: 16.0,
            cbd_pct: 1.0,
            pack_size: '3.5g',
            active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        },
      ],
    };
    
    const batches = mockBatches[productId] || [];
    console.log(`✅ Returning ${batches.length} mock batches`);
    return batches;
  }
  
  const { data, error } = await supabase
    .from('batches')
    .select(`
      *,
      product:products(*)
    `)
    .eq('product_id', productId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

/**
 * Get single batch by ID
 */
export const getBatchById = async (id: string): Promise<BatchWithProduct | null> => {
  const { data, error } = await supabase
    .from('batches')
    .select(`
      *,
      product:products(*)
    `)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
};

/**
 * Get batch by product and batch number
 */
export const getBatchByNumber = async (
  productId: string,
  batchNo: string
): Promise<Batch | null> => {
  const { data, error } = await supabase
    .from('batches')
    .select('*')
    .eq('product_id', productId)
    .eq('batch_no', batchNo)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
};

/**
 * Create batch (admin only)
 */
export const createBatch = async (batch: BatchInsert): Promise<Batch> => {
  const { data, error } = await supabase
    .from('batches')
    .insert(batch)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Update batch (admin only)
 */
export const updateBatch = async (id: string, updates: BatchUpdate): Promise<Batch> => {
  const { data, error } = await supabase
    .from('batches')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Delete batch (admin only)
 */
export const deleteBatch = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('batches')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

/**
 * Get batches by status
 */
export const getBatchesByStatus = async (status: string): Promise<BatchWithProduct[]> => {
  const { data, error } = await supabase
    .from('batches')
    .select(`
      *,
      product:products(*)
    `)
    .eq('status', status)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

/**
 * Get batches expiring soon (within 30 days)
 */
export const getBatchesExpiringSoon = async (): Promise<BatchWithProduct[]> => {
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

  const { data, error } = await supabase
    .from('batches')
    .select(`
      *,
      product:products(*)
    `)
    .not('expiry_date', 'is', null)
    .lte('expiry_date', thirtyDaysFromNow.toISOString())
    .order('expiry_date', { ascending: true });

  if (error) throw error;
  return data || [];
};
